import React, { useRef, useEffect, useCallback } from 'react';
import { getRendererByKey, getForegroundRenderer } from './proceduralBackgrounds';

/**
 * Module-level shared state per renderer key.
 * Ensures seamless handoff when start/end screen unmounts and game screen
 * mounts the same procedural background (same clouds, same time offset).
 */
const sharedStore = new Map();

function getShared(key) {
  if (!sharedStore.has(key)) {
    sharedStore.set(key, { state: {}, time: 0, wallTime: 0, lastTimestamp: null, refCount: 0, fgCanvas: null });
  }
  const entry = sharedStore.get(key);
  entry.refCount++;
  return entry;
}

function releaseShared(key) {
  const entry = sharedStore.get(key);
  if (!entry) return;
  entry.refCount--;
  // Keep state alive briefly so the next mount in the same transition picks it up.
  // Clean up after 2 seconds of no consumers to avoid leaking across campaigns.
  if (entry.refCount <= 0) {
    entry._cleanup = setTimeout(() => {
      if (entry.refCount <= 0) sharedStore.delete(key);
    }, 2000);
  }
}

// Exported aliases for ProceduralForeground to share the same store
export const getSharedState = getShared;
export const releaseSharedState = releaseShared;

/**
 * Register a foreground canvas so ProceduralBackground's RAF loop can
 * drive it without spinning up a second requestAnimationFrame chain.
 */
export function registerForegroundCanvas(key, canvasEl) {
  const entry = sharedStore.get(key);
  if (entry) entry.fgCanvas = canvasEl;
}

export function unregisterForegroundCanvas(key) {
  const entry = sharedStore.get(key);
  if (entry) entry.fgCanvas = null;
}

/**
 * ProceduralBackground — renders an animated canvas background.
 *
 * Drop this behind the game canvas/UI at zIndex 0.
 * It runs a single requestAnimationFrame loop and calls both the
 * background and foreground renderers each frame (if a foreground
 * canvas has been registered via registerForegroundCanvas).
 *
 * State (cloud positions, star arrays, etc.) and time origin are shared
 * across all instances of the same rendererKey so transitions between
 * start screen -> game -> end screen are seamless.
 *
 * Uses clamped delta-time accumulation (max 50ms per frame) to prevent
 * clouds from jumping when the tab is backgrounded and resumed.
 *
 * @param {string}  rendererKey  - e.g. "skyClouds", "nightStars"
 * @param {object}  style        - extra style overrides (optional)
 * @param {boolean} isAnimating  - when false, freezes at current frame (default true)
 * @param {string}  direction        - animation drift direction: 'right'|'left'|'down'|'up' (default 'right')
 * @param {number}  speedMultiplier  - multiplier for animation speed (default 1.0, e.g. 1.15 for subtle parallax boost)
 */
export default function ProceduralBackground({ rendererKey, style, isAnimating = true, direction = null, speedMultiplier = 1 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const sharedRef = useRef(null);
  const frozenTimeRef = useRef(null);
  const speedMultiplierRef = useRef(speedMultiplier);
  const smoothSpeedRef = useRef(speedMultiplier);
  const isAnimatingRef = useRef(isAnimating);
  speedMultiplierRef.current = speedMultiplier;
  isAnimatingRef.current = isAnimating;
  const directionRef = useRef(direction);
  directionRef.current = direction;
  // Cached canvas size — updated by ResizeObserver, avoids getBoundingClientRect per frame
  const sizeRef = useRef({ w: 0, h: 0 });
  const ctxRef = useRef(null);

  const draw = useCallback((timestamp) => {
    const cvs = canvasRef.current;
    const shared = sharedRef.current;
    if (!cvs || !shared) return;
    if (!ctxRef.current) ctxRef.current = cvs.getContext('2d');
    const ctx = ctxRef.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = sizeRef.current.w;
    const h = sizeRef.current.h;

    if (!w || !h) {
      rafRef.current = requestAnimationFrame(draw);
      return;
    }

    // Resize canvas buffer if needed (DPR capped at 1.5 — imperceptible on soft procedural art)
    const bw = Math.round(w * dpr);
    const bh = Math.round(h * dpr);
    if (cvs.width !== bw || cvs.height !== bh) {
      cvs.width = bw;
      cvs.height = bh;
      // Canvas buffer resize clears the context state, re-acquire
      ctxRef.current = cvs.getContext('2d');
    }

    const renderer = getRendererByKey(rendererKey);
    if (!renderer) return;

    // Clamped delta-time accumulation (prevents jumps on tab resume).
    // Two clocks: `time` is accelerated by the gameplay speed multiplier
    // (used for scroll/drift), `wallTime` is real seconds (used for trig
    // animations — sway, twinkle, flicker — that should look the same on
    // start screen and during gameplay).
    if (frozenTimeRef.current == null) {
      if (shared.lastTimestamp != null) {
        const rawDt = (timestamp - shared.lastTimestamp) / 1000;
        const dt = Math.min(rawDt, 0.05); // 50ms clamp
        const target = speedMultiplierRef.current;
        smoothSpeedRef.current += (target - smoothSpeedRef.current) * Math.min(1, dt * 3);
        shared.time += dt * smoothSpeedRef.current;
        shared.wallTime = (shared.wallTime || 0) + dt;
      }
      shared.lastTimestamp = timestamp;
    }

    const time = frozenTimeRef.current != null ? frozenTimeRef.current : shared.time;
    const wallTime = shared.wallTime || 0;

    // ── Draw background ──
    const drawCtx = ctxRef.current;
    drawCtx.save();
    drawCtx.scale(dpr, dpr);
    try {
      renderer(drawCtx, w, h, time, shared.state, directionRef.current, wallTime);
    } catch (e) {
      console.error('[ProceduralBackground] renderer error:', e);
    }
    drawCtx.restore();

    // ── Draw foreground in same RAF tick (no second loop) ──
    const fgCvs = shared.fgCanvas;
    if (fgCvs) {
      const fgRenderer = getForegroundRenderer(rendererKey);
      if (fgRenderer) {
        const fgCtx = fgCvs.getContext('2d');
        const fgW = sizeRef.current.w;
        const fgH = sizeRef.current.h;
        const fgBw = Math.round(fgW * dpr);
        const fgBh = Math.round(fgH * dpr);
        if (fgCvs.width !== fgBw || fgCvs.height !== fgBh) {
          fgCvs.width = fgBw;
          fgCvs.height = fgBh;
        }
        fgCtx.save();
        fgCtx.scale(dpr, dpr);
        try {
          fgRenderer(fgCtx, fgW, fgH, time, shared.state, directionRef.current, wallTime);
        } catch (e) {
          console.error('[ProceduralForeground] renderer error:', e);
        }
        fgCtx.restore();
      }
    }

    if (isAnimatingRef.current || frozenTimeRef.current == null) {
      rafRef.current = requestAnimationFrame(draw);
    } else {
      rafRef.current = null;
    }
  }, [rendererKey]);

  // Freeze/unfreeze animation
  useEffect(() => {
    const shared = sharedRef.current;
    if (!shared) return;
    if (!isAnimating) {
      if (frozenTimeRef.current == null) {
        frozenTimeRef.current = shared.time;
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    } else {
      if (frozenTimeRef.current != null) {
        // Resume from frozen time — reset lastTimestamp so next frame gets a clean dt
        shared.lastTimestamp = null;
        frozenTimeRef.current = null;
      }
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(draw);
      }
    }
  }, [draw, isAnimating]);

  // Observe canvas size changes — replaces per-frame getBoundingClientRect
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const update = () => {
      sizeRef.current = { w: cvs.clientWidth, h: cvs.clientHeight };
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(cvs);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const shared = getShared(rendererKey);
    // Cancel any pending cleanup from a previous unmount
    if (shared._cleanup) { clearTimeout(shared._cleanup); shared._cleanup = null; }
    sharedRef.current = shared;
    frozenTimeRef.current = isAnimatingRef.current ? null : shared.time;
    ctxRef.current = null;
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      releaseShared(rendererKey);
    };
  }, [draw, rendererKey]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
}
